import React, { useState, useRef, useEffect } from "react";
import { Container, Modal, Button, Table, Form, ProgressBar, Spinner, Alert, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { HeadPage, showToast, DropdownFilter } from "src/componentes";
import { ProdutoCadastroSchema } from "./Cadastro/ProdutoCadastro.Dto";
import { ProdutoIncluir, CategoriaBuscarTodos } from "src/services/Estoque.Service";
import { CategoriasCadastroDto } from "../Categorias/Cadastro/CategoriasCadastro.Dto";
import { ConsultarCategoria } from "src/hook/Consulta/ConsultaCategoria.Module";
import { useForm } from "react-hook-form";

interface ImportRow {
  tempId: string;
  codigoProduto: string;
  codigoBarras: string;
  nome: string;
  descricao: string;
  valorUnitarioComercial: string;
  unidadeComercial: string;
  codigoNCM: string;
  categoriaId: number;
  categoriaNome: string;
  checked: boolean;
  errors: Record<string, string>;
  isValid: boolean;
}

const normalizeHeader = (header: string): string => {
  const h = header.trim().toLowerCase();
  if (h.includes("codigoproduto") || h.includes("código") || h === "codigo") return "codigoProduto";
  if (h.includes("codigobarras") || h.includes("barras") || h.includes("gtin")) return "codigoBarras";
  if (h === "nome") return "nome";
  if (h === "descricao" || h === "descrição") return "descricao";
  if (h.includes("valor") || h.includes("unitario") || h.includes("preço")) return "valorUnitarioComercial";
  if (h.includes("unidade") || h === "und" || h === "un") return "unidadeComercial";
  if (h.includes("ncm")) return "codigoNCM";
  return header.trim();
};

const validateSingleRow = (item: any) => {
  const errors: Record<string, string> = {};
  try {
    const valObj = {
      codigoProduto: item.codigoProduto || "",
      codigoBarras: item.codigoBarras || "",
      nome: item.nome || "",
      descricao: item.descricao || "",
      valorUnitarioComercial: item.valorUnitarioComercial || "",
      codigoNCM: item.codigoNCM || "",
      unidadeComercial: item.unidadeComercial || "",
    };
    ProdutoCadastroSchema.validateSync(valObj, { abortEarly: false });
  } catch (err: any) {
    if (err.inner) {
      err.inner.forEach((validationError: any) => {
        if (validationError.path) {
          errors[validationError.path] = validationError.message;
        }
      });
    }
  }

  if (!item.categoriaId) {
    errors["categoriaId"] = "Categoria é obrigatória";
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

const ProdutoImportar = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const consultarCategoria = ConsultarCategoria();
  const [categories, setCategories] = useState<CategoriasCadastroDto[]>([]);
  const [selectedCatId, setSelectedCatId] = useState(0);
  const [selectedCatNome, setSelectedCatNome] = useState("");

  const { register, setValue: setFormValue, formState: { errors: formErrors }, getValues } = useForm({
    defaultValues: {
      categoriaId: 0,
      categoriaNome: "",
    },
  });

  useEffect(() => {
    CategoriaBuscarTodos().then((response) => {
      if (response.data.success) {
        setCategories(response.data.data);
      }
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) return;

      try {
        const parsedData = parseCsvContent(text);
        setRows(parsedData);
        setShowUploadModal(false); // Fechar o modal após carregar
      } catch (err) {
        showToast({
          title: "Erro",
          message: "Falha ao processar o arquivo CSV.",
          variant: "Danger",
        });
      }
    };
    reader.readAsText(selectedFile, "UTF-8");
  };

  const parseCsvContent = (text: string): ImportRow[] => {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return [];

    const headerLine = lines[0];
    const delimiter = headerLine.includes(";") ? ";" : ",";

    const headers = headerLine.split(delimiter).map((h) =>
      h.trim().replace(/^["']|["']$/g, "")
    );

    const defaultCatId = selectedCatId;
    const defaultCatNome = selectedCatNome;

    const parsedRows: ImportRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values: string[] = [];
      let currentVal = "";
      let insideQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"' || char === "'") {
          insideQuotes = !insideQuotes;
        } else if (char === delimiter && !insideQuotes) {
          values.push(currentVal.trim().replace(/^["']|["']$/g, ""));
          currentVal = "";
        } else {
          currentVal += char;
        }
      }
      values.push(currentVal.trim().replace(/^["']|["']$/g, ""));

      const rowObj: any = {};
      headers.forEach((header, index) => {
        const normalized = normalizeHeader(header);
        rowObj[normalized] = values[index] || "";
      });

      if (rowObj.valorUnitarioComercial) {
        let val = rowObj.valorUnitarioComercial;
        if (!val.includes(",") && val.includes(".")) {
          val = val.replace(".", ",");
        }
        rowObj.valorUnitarioComercial = val;
      }

      // Add default category fields for validation check
      const rowWithCat = {
        ...rowObj,
        categoriaId: defaultCatId,
        categoriaNome: defaultCatNome,
      };

      const { isValid, errors } = validateSingleRow(rowWithCat);

      parsedRows.push({
        tempId: `${Date.now()}-${i}-${Math.random()}`,
        codigoProduto: rowObj.codigoProduto || "",
        codigoBarras: rowObj.codigoBarras || "",
        nome: rowObj.nome || "",
        descricao: rowObj.descricao || "",
        valorUnitarioComercial: rowObj.valorUnitarioComercial || "",
        unidadeComercial: rowObj.unidadeComercial || "",
        codigoNCM: rowObj.codigoNCM || "",
        categoriaId: defaultCatId,
        categoriaNome: defaultCatNome,
        checked: isValid,
        errors,
        isValid,
      });
    }
    return parsedRows;
  };

  const handleFieldChange = (
    tempId: string,
    field: keyof Omit<ImportRow, "checked" | "errors" | "isValid" | "tempId">,
    value: string
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.tempId !== tempId) return row;

        const updatedRow = { ...row, [field]: value };
        const { isValid, errors } = validateSingleRow(updatedRow);

        let newChecked = updatedRow.checked;
        if (!isValid) {
          newChecked = false;
        } else if (isValid && !row.isValid) {
          newChecked = true;
        }

        return {
          ...updatedRow,
          isValid,
          errors,
          checked: newChecked,
        };
      })
    );
  };

  const handleCategoryChange = (tempId: string, categoriaId: number, categoriaNome: string) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.tempId !== tempId) return row;

        const updatedRow = { ...row, categoriaId, categoriaNome };
        const { isValid, errors } = validateSingleRow(updatedRow);

        let newChecked = updatedRow.checked;
        if (!isValid) {
          newChecked = false;
        } else if (isValid && !row.isValid) {
          newChecked = true;
        }

        return {
          ...updatedRow,
          isValid,
          errors,
          checked: newChecked,
        };
      })
    );
  };

  const handleToggleRow = (tempId: string) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.tempId !== tempId) return row;
        if (!row.isValid) return row;
        return {
          ...row,
          checked: !row.checked,
        };
      })
    );
  };

  const handleRemoveRow = (tempId: string) => {
    setRows((prevRows) => prevRows.filter((row) => row.tempId !== tempId));
  };

  const getGlobalSelectLabel = () => {
    const validRows = rows.filter((r) => r.isValid);
    if (validRows.length === 0) return "Desmarcar Todos";
    const checkedValid = validRows.filter((r) => r.checked);

    if (checkedValid.length === validRows.length) {
      return "Desmarcar Todos";
    } else if (checkedValid.length === 0) {
      return "Marcar Todos";
    } else {
      return "Inverter Seleção";
    }
  };

  const handleGlobalSelectToggle = () => {
    const validRows = rows.filter((r) => r.isValid);
    if (validRows.length === 0) return;

    const checkedValid = validRows.filter((r) => r.checked);

    setRows((prevRows) =>
      prevRows.map((row) => {
        if (!row.isValid) return row;

        let newChecked = row.checked;
        if (checkedValid.length === validRows.length) {
          newChecked = false;
        } else if (checkedValid.length === 0) {
          newChecked = true;
        } else {
          newChecked = !row.checked;
        }

        return {
          ...row,
          checked: newChecked,
        };
      })
    );
  };

  const handleReset = () => {
    setRows([]);
    setFile(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setShowUploadModal(true);
  };

  const handleImport = async () => {
    const importItems = rows.filter((r) => r.checked && r.isValid);
    if (importItems.length === 0) return;

    setLoading(true);
    setProgress(0);

    let successCount = 0;
    let failCount = 0;

    const parseToFloat = (val: string): number => {
      if (!val) return 0;
      const sanitized = val.replace(/\./g, "").replace(",", ".");
      const parsed = parseFloat(sanitized);
      return isNaN(parsed) ? 0 : parsed;
    };

    for (let i = 0; i < importItems.length; i++) {
      const item = importItems[i];
      const payload: any = {
        id: 0,
        codigoProduto: item.codigoProduto,
        codigoBarras: item.codigoBarras,
        nome: item.nome,
        descricao: item.descricao,
        valorUnitarioComercial: parseToFloat(item.valorUnitarioComercial),
        categoriaId: item.categoriaId,
        categoriaNome: item.categoriaNome,
        codigoNCM: item.codigoNCM,
        unidadeComercial: item.unidadeComercial,
      };

      try {
        const response = await ProdutoIncluir(payload);
        if (response.data.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }

      setProgress(Math.round(((i + 1) / importItems.length) * 100));
    }

    setLoading(false);

    if (successCount > 0) {
      showToast({
        title: "Sucesso",
        message: `${successCount} produtos importados com sucesso!`,
        variant: "Success",
      });
      navigate("/estoque/produtos");
    }

    if (failCount > 0) {
      showToast({
        title: "Erro",
        message: `Falha ao importar ${failCount} produtos.`,
        variant: "Danger",
      });
    }
  };

  const checkedCount = rows.filter((r) => r.checked).length;
  const invalidCount = rows.filter((r) => !r.isValid).length;

  return (
    <Container>
      <HeadPage headText="Importar Produtos" onBack={() => navigate("/estoque/produtos")} />

      {rows.length === 0 ? (
        <div className="text-center py-5">
          <h3>Nenhum arquivo carregado</h3>
          <p className="text-body-secondary mb-4">Selecione um arquivo CSV com os dados dos produtos para iniciar a importação.</p>
          <Button variant="primary" onClick={() => setShowUploadModal(true)}>
            <FontAwesomeIcon icon={faUpload} className="me-2" /> Selecionar Arquivo CSV
          </Button>
        </div>
      ) : (
        <div className="mt-3">
          <Row className="align-items-center mb-3">
            <Col>
              <strong>Arquivo carregado:</strong> {file?.name}
            </Col>
            <Col xs="auto" className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleGlobalSelectToggle}
                disabled={loading || rows.filter((r) => r.isValid).length === 0}
              >
                {getGlobalSelectLabel()}
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setShowUploadModal(true)} disabled={loading}>
                Alterar Arquivo
              </Button>
              <Button variant="outline-danger" size="sm" onClick={handleReset} disabled={loading}>
                Limpar Tudo
              </Button>
            </Col>
          </Row>

          {invalidCount > 0 && (
            <Alert variant="warning">
              Atenção: Existem <strong>{invalidCount}</strong> linha(s) com erros de validação. Edite os valores diretamente na tabela abaixo para corrigi-los. Apenas linhas válidas e marcadas serão importadas.
            </Alert>
          )}

          <div className="table-responsive border rounded mb-3" style={{ maxHeight: "550px", overflowY: "auto" }}>
            <Table striped bordered hover size="sm" className="align-middle mb-0">
              <thead className="table-dark sticky-top">
                <tr>
                  <th className="text-center" style={{ width: "50px" }}>Importar</th>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th style={{ width: "160px" }}>Categoria</th>
                  <th style={{ width: "120px" }}>Valor Unit.</th>
                  <th style={{ width: "80px" }}>Und.</th>
                  <th style={{ width: "120px" }}>NCM</th>
                  <th>Cód. Barras</th>
                  <th className="text-center" style={{ width: "50px" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.tempId}
                    style={
                      !row.isValid
                        ? {
                            backgroundColor: "rgba(220, 53, 69, 0.15)",
                            ["--bs-table-bg" as any]: "rgba(220, 53, 69, 0.15)",
                          }
                        : undefined
                    }
                  >
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={row.checked}
                        disabled={!row.isValid || loading}
                        onChange={() => handleToggleRow(row.tempId)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.codigoProduto}
                        isInvalid={!!row.errors.codigoProduto}
                        disabled={loading}
                        onChange={(e) => handleFieldChange(row.tempId, "codigoProduto", e.target.value)}
                      />
                      {row.errors.codigoProduto && (
                        <div className="invalid-feedback d-block small">{row.errors.codigoProduto}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.nome}
                        isInvalid={!!row.errors.nome}
                        disabled={loading}
                        onChange={(e) => handleFieldChange(row.tempId, "nome", e.target.value)}
                      />
                      {row.errors.nome && (
                        <div className="invalid-feedback d-block small">{row.errors.nome}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.descricao}
                        disabled={loading}
                        onChange={(e) => handleFieldChange(row.tempId, "descricao", e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.categoriaId || ""}
                        isInvalid={!!row.errors.categoriaId}
                        disabled={loading}
                        onChange={(e) => {
                          const catId = Number(e.target.value);
                          const cat = categories.find((c) => c.id === catId);
                          handleCategoryChange(row.tempId, catId, cat ? cat.nome : "");
                        }}
                      >
                        <option value="">Selecione...</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.nome}
                          </option>
                        ))}
                      </Form.Select>
                      {row.errors.categoriaId && (
                        <div className="invalid-feedback d-block small">{row.errors.categoriaId}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.valorUnitarioComercial}
                        isInvalid={!!row.errors.valorUnitarioComercial}
                        disabled={loading}
                        onChange={(e) => handleFieldChange(row.tempId, "valorUnitarioComercial", e.target.value)}
                      />
                      {row.errors.valorUnitarioComercial && (
                        <div className="invalid-feedback d-block small">{row.errors.valorUnitarioComercial}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.unidadeComercial}
                        isInvalid={!!row.errors.unidadeComercial}
                        disabled={loading}
                        onChange={(e) => handleFieldChange(row.tempId, "unidadeComercial", e.target.value)}
                      />
                      {row.errors.unidadeComercial && (
                        <div className="invalid-feedback d-block small">{row.errors.unidadeComercial}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.codigoNCM}
                        isInvalid={!!row.errors.codigoNCM}
                        disabled={loading}
                        onChange={(e) => handleFieldChange(row.tempId, "codigoNCM", e.target.value)}
                      />
                      {row.errors.codigoNCM && (
                        <div className="invalid-feedback d-block small">{row.errors.codigoNCM}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={row.codigoBarras}
                        isInvalid={!!row.errors.codigoBarras}
                        disabled={loading}
                        onChange={(e) => handleFieldChange(row.tempId, "codigoBarras", e.target.value)}
                      />
                      {row.errors.codigoBarras && (
                        <div className="invalid-feedback d-block small">{row.errors.codigoBarras}</div>
                      )}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="link"
                        className="text-danger p-0"
                        disabled={loading}
                        onClick={() => handleRemoveRow(row.tempId)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {loading && (
            <div className="mb-3">
              <ProgressBar now={progress} label={`${progress}%`} animated />
              <div className="text-center mt-2 text-muted">
                Importando produtos, por favor aguarde...
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mb-5">
            <Button variant="outline-secondary" onClick={() => navigate("/estoque/produtos")} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={loading || checkedCount === 0}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Importando...
                </>
              ) : (
                `Importar (${checkedCount})`
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Modal para seleção do arquivo */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered backdrop={rows.length === 0 ? "static" : true}>
        <Modal.Header closeButton={rows.length > 0}>
          <Modal.Title>Selecionar Arquivo CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Label className="fw-bold">Categoria Padrão para os Itens</Form.Label>
            <DropdownFilter<CategoriasCadastroDto>
              fetchData={consultarCategoria.CategoriaBuscar}
              loading={consultarCategoria.loading}
              labelDescription="Pesquise a categoria..."
              getDescription={(item: CategoriasCadastroDto) => item.nome}
              getId={(item: CategoriasCadastroDto) => item.id}
              registeredFields={{
                id: register("categoriaId"),
                description: register("categoriaNome"),
              }}
              errorsMessageDescription={formErrors.categoriaId}
              onSelect={(item: CategoriasCadastroDto | undefined) => {
                if (item !== undefined) {
                  setFormValue("categoriaId", item.id);
                  setFormValue("categoriaNome", item.nome);
                  setSelectedCatId(item.id);
                  setSelectedCatNome(item.nome);
                } else {
                  setFormValue("categoriaId", 0);
                  setFormValue("categoriaNome", "");
                  setSelectedCatId(0);
                  setSelectedCatNome("");
                }
              }}
            />
          </div>

          <div
            className={`d-flex flex-column align-items-center justify-content-center border border-secondary rounded p-5 bg-body-secondary ${!selectedCatId ? "opacity-50" : ""}`}
            style={{ cursor: selectedCatId ? "pointer" : "not-allowed", borderStyle: "dashed" }}
            onClick={() => {
              if (selectedCatId) {
                fileInputRef.current?.click();
              } else {
                showToast({
                  title: "Atenção",
                  message: "Por favor, selecione uma Categoria Padrão primeiro.",
                  variant: "Warning",
                });
              }
            }}
          >
            <FontAwesomeIcon icon={faUpload} size="3x" className="text-secondary mb-3" />
            <h5>Selecione o arquivo CSV</h5>
            <p className="text-body-secondary text-center small">
              O arquivo deve conter as colunas: <strong>codigoProduto, nome, descricao, valorUnitarioComercial, unidadeComercial, codigoNCM, codigoBarras</strong>.
            </p>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".csv"
              disabled={!selectedCatId}
              onChange={handleFileChange}
            />
          </div>
        </Modal.Body>
        {rows.length > 0 && (
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowUploadModal(false)}>
              Fechar
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </Container>
  );
};

export default ProdutoImportar;
